import * as THREE from 'three';

/**
 * Toetsenbord- en muisinvoer.
 *
 * Het richten gebeurt zoals in de oorspronkelijke technische opzet: een ray vanuit de
 * camera door de muispositie, gesneden met een onzichtbaar grondvlak. Het snijpunt is het
 * doelpunt. Onder een isometrische camera voelt dat intuïtief ondanks de schuinte.
 */

/** Hoogte waarop de aim-ray het grondvlak snijdt: borsthoogte, niet de vloer. */
const AIM_PLANE_HEIGHT = 1.2;

export class Input {
  /** Ingedrukte toetsen, genormaliseerd naar lowercase `KeyboardEvent.key`. */
  readonly #keys = new Set<string>();
  readonly #pressedThisStep = new Set<string>();

  /** Muispositie in normalized device coordinates (−1..1). */
  readonly pointerNdc = new THREE.Vector2();

  /** Wereldpunt waar de muis naar wijst, op AIM_PLANE_HEIGHT. */
  readonly aimPoint = new THREE.Vector3();

  #primaryDown = false;
  #primaryPressedThisStep = false;

  readonly #raycaster = new THREE.Raycaster();
  readonly #aimPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -AIM_PLANE_HEIGHT);
  readonly #detach: Array<() => void> = [];

  constructor(element: HTMLElement) {
    this.#listen(window, 'keydown', (event) => {
      const key = (event as KeyboardEvent).key.toLowerCase();
      if (!this.#keys.has(key)) this.#pressedThisStep.add(key);
      this.#keys.add(key);
    });

    this.#listen(window, 'keyup', (event) => {
      this.#keys.delete((event as KeyboardEvent).key.toLowerCase());
    });

    // Bij focusverlies alle toetsen loslaten, anders blijft de speler doorlopen.
    this.#listen(window, 'blur', () => {
      this.#keys.clear();
      this.#primaryDown = false;
    });

    this.#listen(element, 'pointermove', (event) => {
      const { clientX, clientY } = event as PointerEvent;
      const rect = element.getBoundingClientRect();
      this.pointerNdc.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
    });

    this.#listen(element, 'pointerdown', (event) => {
      if ((event as PointerEvent).button !== 0) return;
      if (!this.#primaryDown) this.#primaryPressedThisStep = true;
      this.#primaryDown = true;
    });

    this.#listen(window, 'pointerup', (event) => {
      if ((event as PointerEvent).button === 0) this.#primaryDown = false;
    });

    this.#listen(element, 'contextmenu', (event) => event.preventDefault());
  }

  /** Is de toets nu ingedrukt? */
  isDown(key: string): boolean {
    return this.#keys.has(key.toLowerCase());
  }

  /** Is de toets in deze simulatiestap voor het eerst ingedrukt? */
  wasPressed(key: string): boolean {
    return this.#pressedThisStep.has(key.toLowerCase());
  }

  get firing(): boolean {
    return this.#primaryDown;
  }

  get firePressed(): boolean {
    return this.#primaryPressedThisStep;
  }

  /**
   * Bewegingsrichting in wereld-XZ, al geroteerd naar de camerahoek zodat "omhoog" op het
   * scherm ook omhoog op het scherm beweegt. Lengte is 0 of 1.
   */
  movement(out: THREE.Vector2, cameraYaw: number): THREE.Vector2 {
    let x = 0;
    let z = 0;
    if (this.isDown('w') || this.isDown('arrowup')) z -= 1;
    if (this.isDown('s') || this.isDown('arrowdown')) z += 1;
    if (this.isDown('a') || this.isDown('arrowleft')) x -= 1;
    if (this.isDown('d') || this.isDown('arrowright')) x += 1;

    if (x === 0 && z === 0) return out.set(0, 0);

    const length = Math.hypot(x, z);
    x /= length;
    z /= length;

    const sin = Math.sin(cameraYaw);
    const cos = Math.cos(cameraYaw);
    return out.set(x * cos + z * sin, -x * sin + z * cos);
  }

  /** Werk `aimPoint` bij op basis van de huidige muispositie. */
  updateAim(camera: THREE.Camera): void {
    this.#raycaster.setFromCamera(this.pointerNdc, camera);
    if (!this.#raycaster.ray.intersectPlane(this.#aimPlane, this.aimPoint)) {
      // Ray loopt evenwijdig aan het vlak: houd het vorige punt vast.
      return;
    }
  }

  /** Aan het einde van elke simulatiestap aanroepen: wist de "net ingedrukt"-vlaggen. */
  endStep(): void {
    this.#pressedThisStep.clear();
    this.#primaryPressedThisStep = false;
  }

  dispose(): void {
    for (const detach of this.#detach) detach();
    this.#detach.length = 0;
    this.#keys.clear();
  }

  #listen(target: EventTarget, type: string, handler: (event: Event) => void): void {
    target.addEventListener(type, handler);
    this.#detach.push(() => target.removeEventListener(type, handler));
  }
}
